import { useCallback } from 'react';

import { GridCellKind, getMiddleCenterBias } from '@glideapps/glide-data-grid';

import type { CustomRenderer } from '@glideapps/glide-data-grid';

import { useMount } from '@simplywallst/ui-core';

import type { CustomCellEditor, CustomCellProps as Props } from '../types';

import { cn } from '@simplywallst/ui-core/next';
import ChevronDownSmallSolid from '@simplywallst/ui-core/icons/ChevronDownSmallSolid';

import { Menu, Item } from '@/components/Menu';

import { isCustomCellOfKind } from '../utils';

import { getAllowedValue, validatePastedValue } from './utils';

import type { MenuCell } from './types';

import { kind } from './types';

const Cell = ({ value: cell, onFinishedEditing }: Props<MenuCell>) => {
  const value = cell.data.value;
  const selectedKeys = value ? [value] : undefined;

  const handleAction = useCallback(
    (key: number | string | null) => {
      const value = String(key);
      onFinishedEditing(
        {
          ...cell,
          data: {
            ...cell.data,
            value,
            error: null,
          },
        },
        [1, 0]
      );
    },
    [onFinishedEditing, cell]
  );
  return (
    <div className="grid h-[53px] items-center px-x0_75">
      <Menu
        label={
          <button
            aria-label="More Options"
            autoFocus={true}
            className="grid grid-cols-[auto_max-content] items-center justify-between pl-x1"
          >
            <span className={cn(value === 'buy' ? 'text-good' : 'text-bad')}>
              {(value ?? '').toUpperCase()}
            </span>
            <ChevronDownSmallSolid className="fill-legacy-mediumgray" />
          </button>
        }
        defaultOpen={true}
        placement="bottom start"
        selectedKeys={selectedKeys}
        selectionMode="single"
        onAction={handleAction}
      >
        <Item textValue="BUY" key="buy">
          <p className="m-0 text-xs text-good">
            <span>BUY</span>
          </p>
        </Item>
        <Item textValue="SELL" key="sell">
          <p className="m-0 text-xs text-bad">
            <span>SELL</span>
          </p>
        </Item>
      </Menu>
    </div>
  );
};

/**
 * Allow the component to settle and only call onFinishedEditing onMount
 *
 * Fixes:
 *
 * Warning: Cannot update a component (`ForwardRef(DataEditorImpl)`) while
 * rendering a different component (`Editor`). To locate the bad setState()
 * call inside `Editor`, follow the stack trace as described in
 * https://reactjs.org/link/setstate-in-render Error Component Stack
 */
const DumbRender = ({ onMount }: { onMount: () => void }) => {
  useMount(() => onMount());
  return null;
};

const Editor: CustomCellEditor<MenuCell> = ({
  value: cell,
  initialValue,
  onFinishedEditing,
  ...rest
}: Props<MenuCell>) => {
  let value: string | null = null;

  if (initialValue === 'b') {
    value = 'buy';
  }

  if (initialValue === 's') {
    value = 'sell';
  }

  // If a valid initialValue was supplied and mapped, return early
  if (value) {
    return (
      <DumbRender
        onMount={() =>
          onFinishedEditing(
            {
              ...cell,
              data: {
                ...cell.data,
                value,
                error: null,
              },
            },
            [1, 0]
          )
        }
      />
    );
  }

  // Let the user select the value
  return <Cell value={cell} onFinishedEditing={onFinishedEditing} {...rest} />;
};

export const Render: CustomRenderer<MenuCell> = {
  kind: GridCellKind.Custom,
  isMatch: isCustomCellOfKind(kind),
  draw: (args, cell) => {
    const { ctx, theme, rect } = args;
    const { value, error } = cell.data;

    const isError = error !== null;

    if (isError) {
      ctx.beginPath();
      ctx.fillStyle = 'rgba(230, 65, 65, 0.1)';
      ctx.rect(rect.x + 1, rect.y + 1, rect.width, rect.height - 1);
      ctx.fill();
      ctx.fillStyle = 'rgba(230, 65, 65, 1)';
      ctx.fillText(
        error,
        rect.x + theme.cellHorizontalPadding,
        rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme)
      );
    } else {
      const text = getAllowedValue(
        cell.data.allowedValues,
        value
      ).toUpperCase();
      ctx.fillStyle =
        text === 'BUY' ? 'rgba(17, 165, 94, 1)' : 'rgba(230, 65, 65, 1)';
      ctx.fillText(
        text,
        rect.x + theme.cellHorizontalPadding,
        rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme)
      );
    }
    return true;
  },
  measure: (ctx, cell, theme) => {
    const { value } = cell.data;
    return (
      (value ? ctx.measureText(value).width : 0) +
      theme.cellHorizontalPadding * 2
    );
  },
  provideEditor: () => ({
    editor: Editor,
    disablePadding: true,
  }),
  onPaste: validatePastedValue,
};
